import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { Link, useParams } from 'react-router-dom';
import { useStores } from '../../hooks/use-stores';
import ThemedSpinner from '../../components/themed/ThemedSpinner';
import {
    Block,
    SpaceBetweenDiv,
    BigModal,
    ThemedButton,
    Heading2,
    TextDefault,
    FlexDiv,
} from '@awarego-ui/components';
import TableClient from '../../components/table/TableClient';
import {
    extractErrorMessage,
    formatCurrency,
} from '../../utils/helpers';
import TextField from '@mui/material/TextField';

/**
 * Partner-admin distributors list (nested partners).
 * Create a distributor, then manage its companies / settings / admins
 * via the existing partner pages under /partner/:distributorId/...
 */
function PartnerDistributors() {
    const { partnerId } = useParams();
    const { commonStore, partnerStore } = useStores();
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [name, setName] = useState('');
    const [createError, setCreateError] = useState(null);

    const {
        subPartners,
        loadingSubPartners,
        creatingSubPartner,
        canManageDistributors,
    } = partnerStore;

    useEffect(() => {
        void partnerStore.loadSettings(partnerId);
        void partnerStore.loadSubPartners(partnerId);
    }, [partnerId, partnerStore]);

    const columns = useMemo(
        () => [
            {
                Header: 'Name',
                accessor: 'name',
                Cell: ({ row }) => (
                    <Link to={`/partner/${row.original.id}/companies`}>
                        {row.original.name}
                    </Link>
                ),
            },
            {
                Header: 'Companies',
                accessor: 'companiesCount',
                Cell: ({ value }) => value ?? 0,
            },
            {
                Header: 'Seats',
                accessor: 'seatsCount',
                Cell: ({ value }) => value ?? 0,
            },
            {
                Header: 'Monthly cost',
                accessor: 'monthlyCost',
                Cell: ({ row, value }) =>
                    formatCurrency(
                        value ?? 0,
                        row.original.currencyCode
                    ),
                tooltip:
                    'Allocated share of your Chargebee billing for this distributor’s companies (seat-based). Use this to bill the distributor on your side.',
            },
            {
                Header: ' ',
                Cell: ({ row }) => {
                    const id = row.original.id;
                    return (
                        <FlexDiv gap={12} alignCenter>
                            <Link to={`/partner/${id}/companies`}>
                                Companies
                            </Link>
                            <Link to={`/partner/${id}/settings`}>
                                Settings
                            </Link>
                            <Link to={`/partner/${id}/admins`}>Admins</Link>
                        </FlexDiv>
                    );
                },
            },
        ],
        []
    );

    const openCreate = () => {
        setName('');
        setCreateError(null);
        setShowCreateModal(true);
    };

    const doCreate = async () => {
        setCreateError(null);
        const trimmed = (name || '').trim();
        if (trimmed.length < 4) {
            setCreateError('Name must be at least 4 characters');
            return;
        }
        try {
            const created = await partnerStore.createSubPartner(partnerId, {
                name: trimmed,
            });
            setShowCreateModal(false);
            commonStore.success(
                created?.name
                    ? `Distributor “${created.name}” created`
                    : 'Distributor created'
            );
        } catch (e) {
            const msg = extractErrorMessage(e);
            setCreateError(msg);
            commonStore.error(msg);
        }
    };

    if (loadingSubPartners && !partnerStore.settingsLoaded) {
        return <ThemedSpinner />;
    }

    if (!canManageDistributors) {
        return (
            <Block>
                <Heading2 displayBlock>Distributors</Heading2>
                <TextDefault lighter mt={16} ml={16} displayBlock>
                    Nested partners are not enabled for this account, or you are
                    viewing a distributor.
                </TextDefault>
            </Block>
        );
    }

    const totalMonthly = (subPartners || []).reduce(
        (sum, sp) => sum + (Number(sp.monthlyCost) || 0),
        0
    );
    const currencyCode =
        (subPartners || []).find((sp) => sp.currencyCode)?.currencyCode ||
        null;

    return (
        <Fragment>
            <Block>
                <SpaceBetweenDiv ml={16} pr={32}>
                    <div>
                        <Heading2 displayBlock>Distributors</Heading2>
                        <TextDefault lighter displayBlock>
                            Create distributors and manage their companies,
                            settings, and admins. Monthly cost is their
                            allocated share of your billing.
                        </TextDefault>
                        {subPartners.length > 0 && (
                            <TextDefault displayBlock mt={8}>
                                Total allocated:{' '}
                                {formatCurrency(totalMonthly, currencyCode)}
                                /month across {subPartners.length} distributor
                                {subPartners.length === 1 ? '' : 's'}
                            </TextDefault>
                        )}
                    </div>
                    <ThemedButton
                        mode="secondary"
                        text="Add a distributor"
                        onClick={openCreate}
                    />
                </SpaceBetweenDiv>
                {loadingSubPartners ? (
                    <ThemedSpinner />
                ) : subPartners.length > 0 ? (
                    <TableClient
                        columns={columns}
                        data={subPartners}
                        hidePagination={subPartners.length < 10}
                        initialPageSize={10}
                    />
                ) : (
                    <TextDefault lighter ml={16} mt={16} displayBlock>
                        No distributors yet. Add one to let them manage their
                        own companies under your billing.
                    </TextDefault>
                )}
            </Block>
            <BigModal
                isOpen={showCreateModal}
                onRequestClose={() => setShowCreateModal(false)}
                heading="Add a distributor"
                modalContent={
                    <div style={{ padding: '8px 0' }}>
                        <TextDefault lighter displayBlock mb={8}>
                            Distributors manage their own companies. Pricing and
                            billing stay with you.
                        </TextDefault>
                        <TextField
                            fullWidth
                            label="Distributor name"
                            variant="filled"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            error={!!createError}
                            helperText={createError || ' '}
                        />
                    </div>
                }
                modalFooterContent={
                    creatingSubPartner ? (
                        <ThemedButton
                            mode="primary"
                            type="button"
                            disabled
                            $saving={true}
                            text="Saving"
                            $center={true}
                        />
                    ) : (
                        <Fragment>
                            <ThemedButton
                                mode="secondary"
                                text="Cancel"
                                onClick={() => setShowCreateModal(false)}
                            />
                            <ThemedButton
                                mode="primary"
                                text="Create"
                                onClick={doCreate}
                            />
                        </Fragment>
                    )
                }
            />
        </Fragment>
    );
}

export default observer(PartnerDistributors);
